import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@apollo/client";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useTranslation} from "react-i18next";

import {
    GetSearchHitListDocument
} from "../../../../packages/graphql-layer/src/queries/__generated__/GetSearchHitList";
import {getQueryParam} from "../../../utility/navigationUtil";
import {buttonStyle} from "../../../common/constants/buttonStyle";
import {usePageContext} from "../../../context/PageContext";

import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCOContainer from "../../fundamentals/CCOContainer";
import CCOLoadingSpinner from "../../fundamentals/CCOLoadingSpinner";

import {CmTeasableType} from "../../types";
import C018StandardTeaser from "../C018StandardTeaser/C018StandardTeaser";
import C036SearchField from "./C036SearchField";
import C036SearchFilter from "./C036SearchFilter";

const query = GetSearchHitListDocument;

const FALLBACK_DOC_TYPES = [
    "CMChannel",
    "SOSPublication",
    "SOSNews",
    "SOSPressrelease",
    "SOSSpecialTopic"
];

const ALWAYS_INCLUDED = ["CMChannel"];

const HIT_LIST_LIMIT = 10;

type FilterValues = Record<
    string,
    {
        value: string;
        isActive: boolean;
    }
>;

/*
 * Result pages keyed by their offset, so a page that is delivered twice
 * overwrites itself instead of being appended a second time.
 */
type ResultPages = {
    queryKey: string;
    pages: Record<number, CmTeasableType[]>;
};

// The token tells a restarted search apart from the identical one before it.
const buildQueryKey = (
    restartToken: number,
    search: string,
    docTypes: string[]
): string =>
    JSON.stringify([restartToken, search, [...docTypes].sort()]);

const flattenPages = (
    pages: Record<number, CmTeasableType[]>
): CmTeasableType[] =>
    Object.keys(pages)
        .map(Number)
        .sort((a, b) => a - b)
        .flatMap(pageOffset => pages[pageOffset]);

const getDocTypesForQuery = (
    filterValues: FilterValues,
    docTypesFromFilters: string[],
    fallbackDocTypes: string[],
    alwaysIncluded: string[]
): string[] => {
    const activeFilters = Object.values(filterValues)
        .filter(({isActive}) => isActive)
        .map(({value}) => value);

    if (activeFilters.length > 0) {
        return Array.from(new Set(activeFilters));
    }

    // When no filters are active, combine filter doctypes
    // and always-included doctypes.
    const combinedDocTypes = new Set([
                                         ...(
                                             docTypesFromFilters.length > 0
                                                 ? docTypesFromFilters
                                                 : fallbackDocTypes
                                         ),
                                         ...alwaysIncluded
                                     ]);

    return Array.from(combinedDocTypes);
};

function C036SearchAction() {
    const pageContext = usePageContext();
    const {t} = useTranslation();

    const filterSettings = (
        pageContext?.page?.searchSettings?.["sos.doctype.filter"] ?? {}
    ) as Record<string, string>;

    const showFilterSetting =
        pageContext?.page?.searchSettings?.["sos.search.showFilter"];

    const sid = pageContext.siteId;

    const initialFilterValues: FilterValues = Object.fromEntries(
        Object.keys(filterSettings).map(key => [
            key,
            {
                value: filterSettings[key],
                isActive: false
            }
        ])
    );

    const [filterValues, setFilterValues] =
        useState<FilterValues>(initialFilterValues);

    const [search, setSearch] = useState("");
    const [offset, setOffset] = useState(0);
    const [restartToken, setRestartToken] = useState(0);

    const [resultPages, setResultPages] = useState<ResultPages>({
        queryKey: "",
        pages: {}
    });

    const docTypesFromFilters = Object.values(initialFilterValues)
        .map(item => item.value);

    const showFilter =
        Boolean(showFilterSetting) &&
        docTypesFromFilters.length > 0;

    const docTypesForQuery = getDocTypesForQuery(
        filterValues,
        docTypesFromFilters,
        FALLBACK_DOC_TYPES,
        ALWAYS_INCLUDED
    );

    // A new search term, a new filter or a restart starts a new result list.
    const queryKey = buildQueryKey(restartToken, search, docTypesForQuery);

    /*
     * Initialize search value from URL.
     */
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const searchString = window.location.search;
        const fieldValue = getQueryParam(searchString, "search");

        if (fieldValue) {
            setSearch(fieldValue);
        }
    }, []);

    const {
        data,
        loading,
        error
    } = useQuery(query, {
        variables: {
            search,
            offset,
            limit: HIT_LIST_LIMIT,
            siteId: sid,
            docTypes: docTypesForQuery
        },
        fetchPolicy: "network-only"
    });

    /*
     * Handle Apollo errors.
     */
    useEffect(() => {
        if (!error) {
            return;
        }

        console.error("Can't load search hits:", error);
    }, [error]);

    /*
     * Store an incoming result page. A response belonging to another query
     * replaces the list instead of extending it.
     */
    useEffect(() => {
        if (loading || !data) {
            return;
        }

        const results =
            (data?.content?.search?.result ?? []) as CmTeasableType[];

        setResultPages(previousResultPages => ({
            queryKey,
            pages: previousResultPages.queryKey === queryKey
                ? {...previousResultPages.pages, [offset]: results}
                : {[offset]: results}
        }));
    }, [data, loading, queryKey, offset]);

    const hits = useMemo(
        () => flattenPages(resultPages.pages),
        [resultPages]
    );

    // Start at the first result page again, dropping the pages loaded so far.
    const restart = () => {
        setOffset(0);
        setRestartToken(previousToken => previousToken + 1);
    };

    /*
     * Load the next result page.
     */
    const loadMore = () => {
        setOffset(previousOffset =>
                      previousOffset + HIT_LIST_LIMIT
        );
    };

    /*
     * Start a completely new search.
     */
    const loadNew = (input?: string) => {
        if (!input) {
            return;
        }

        restart();
        setSearch(input);
    };

    /*
     * Toggle a filter and start again at the first result page.
     *
     * Resetting the pagination happens directly here instead
     * of indirectly through a useEffect on filterValues.
     */
    const handleFilterChange = (key: string) => {
        restart();

        setFilterValues(previousFilterValues => ({
            ...previousFilterValues,
            [key]: {
                ...previousFilterValues[key],
                isActive: !previousFilterValues[key].isActive
            }
        }));
    };

    /*
     * Reset all filters and start again at the first result page.
     */
    const handleResetFilters = () => {
        restart();
        setFilterValues(initialFilterValues);
    };

    const searchHits = hits.map((teaser, index) => (
        <C018StandardTeaser
            dataCypress="searchResult"
            key={"h" + index}
            cmTeaser={teaser}
        />
    ));

    const totalNumberResults =
        data?.content?.search?.numFound ?? null;

    const subline =
        t("search.hit_first_part") +
        search +
        t("search.hit_second_part") +
        totalNumberResults +
        t("search.hit_third_part") +
        (totalNumberResults > 0 ? ":" : "");

    const hasMoreResults =
        hits.length > 0 &&
        totalNumberResults !== null &&
        totalNumberResults - hits.length > 0;

    return (
        <>
            <CCOComponentWrapper
                background="grey"
                smallmargin={true}
                noTopMargin={true}
            >
                <CCOHeadline
                    id="suchcontainer"
                    headline={t("search.result_label")}
                />

                <CCOContainer width="medium">
                    <Grid
                        container
                        sx={{
                            marginY: 6,
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column"
                        }}
                    >
                        <Grid key="searchfield">
                            <C036SearchField
                                dataCypress="resultSearchField"
                                val={search}
                                isInContent={true}
                                reloadFunction={loadNew}
                            />
                        </Grid>
                    </Grid>
                </CCOContainer>

                {showFilter ? (
                    <C036SearchFilter
                        filterValues={filterValues}
                        handleFilterChange={handleFilterChange}
                        handleResetFilters={handleResetFilters}
                    />
                ) : <></>}
            </CCOComponentWrapper>

            <CCOComponentWrapper>
                <CCOContainer width="medium">
                    <Grid container>
                        <Grid
                            size={{xs: 12}}
                            key="result"
                        >
                            <Typography
                                variant="h3"
                                align="center"
                                sx={{
                                    marginBottom: 6
                                }}
                            >
                                {!loading ? subline : null}
                            </Typography>
                        </Grid>
                    </Grid>
                </CCOContainer>

                <CCOContainer
                    dataCypress="searchResultContainer"
                    width="medium"
                >
                    {!loading ? (
                        searchHits
                    ) : (
                        <CCOContainer width="medium">
                            <CCOLoadingSpinner/>
                        </CCOContainer>
                    )}
                </CCOContainer>

                {hasMoreResults ? (
                    <CCOContainer width="s">
                        <Grid
                            container
                            sx={{
                                marginY: 6,
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column"
                            }}
                        >
                            <Grid>
                                <Button
                                    key="btn"
                                    variant="contained"
                                    sx={{
                                        ...buttonStyle.green
                                    }}
                                    startIcon={
                                        <ExpandMoreIcon/>
                                    }
                                    onClick={loadMore}
                                >
                                    {t("coo_show_more")}
                                </Button>
                            </Grid>
                        </Grid>
                    </CCOContainer>
                ) : <></>}
            </CCOComponentWrapper>
        </>
    );
}

export default C036SearchAction;